import React from "react";
import PropTypes from "prop-types";
import StatusMetricBlock from "./StatusMetricBlock";
import "./activityAbnormalities.scss";

/** Format activity count as "1 time / 7 days" or "2 times / 7 days" */
const formatActivityFrequency = (count) =>
  `${count} ${count === 1 ? "time" : "times"} / 7 days`;

/**
 * ActivityAbnormalities Component
 *
 * Shared component used in CarePlan (Activity section) and ActivityMonitoring (Abnormality card).
 * Displays fall alerts and abnormalities using StatusMetricBlock.
 *
 * When both fall and pfall counts are 0 AND there are no nightWandering events
 * AND no abnormalities, shows a "No Abnormalities Detected" banner.
 */
const ActivityAbnormalities = ({ fallAlerts, abnormalities = [] }) => {
  const fallCount = fallAlerts?.fall?.count ?? 0;
  const pfallCount = fallAlerts?.pfall?.count ?? 0;
  const nightWandering = fallAlerts?.nightWandering;
  const nightWanderingCount = nightWandering?.count ?? 0;

  const hasNoAbnormalities =
    fallCount === 0 &&
    pfallCount === 0 &&
    (nightWandering == null || nightWanderingCount === 0) &&
    abnormalities.length === 0;

  if (hasNoAbnormalities) {
    return (
      <div className="activity-abnormalities">
        <div className="no-abnormalities-banner">
          <div className="no-abnormalities-icon">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <span>No Abnormalities Detected</span>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-abnormalities">
      {/* Fall Alerts */}
      <div className="metrics-grid metrics-grid-full">
        {fallCount > 0 ? (
          <StatusMetricBlock
            label="Fall"
            value={formatActivityFrequency(fallCount)}
            status={fallAlerts.fall.status}
          />
        ) : (
          pfallCount > 0 && (
            <StatusMetricBlock
              label="Possible Fall"
              value={formatActivityFrequency(pfallCount)}
              status={fallAlerts.pfall.status}
            />
          )
        )}
        {nightWandering != null && (
          <StatusMetricBlock
            label="Night Wandering"
            value={formatActivityFrequency(nightWandering.count)}
            status={nightWandering.status}
          />
        )}
      </div>

      {/* Abnormalities */}
      {abnormalities.length > 0 && (
        <div
          className="metrics-grid metrics-grid-full"
          style={{ marginTop: "12px" }}
        >
          {abnormalities.map((abnormality) => (
            <StatusMetricBlock
              key={abnormality.type}
              label={abnormality.label}
              value={abnormality.description}
              status={abnormality.status}
            />
          ))}
        </div>
      )}
    </div>
  );
};

ActivityAbnormalities.propTypes = {
  fallAlerts: PropTypes.shape({
    fall: PropTypes.shape({
      count: PropTypes.number,
      status: PropTypes.string,
    }),
    pfall: PropTypes.shape({
      count: PropTypes.number,
      status: PropTypes.string,
    }),
    nightWandering: PropTypes.shape({
      count: PropTypes.number,
      status: PropTypes.string,
    }),
  }),
  abnormalities: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      label: PropTypes.string,
      description: PropTypes.string,
      status: PropTypes.string,
    }),
  ),
};

export default ActivityAbnormalities;
